'use client'

import { memo, Suspense, useEffect, useState } from 'react'
import { Canvas } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { Lights } from './lights'
import MainScene from './mainScene'
import RendererDither from './effects/rendererDither'
import { PostFX } from './effects/PostFX'
import { Precompiler, holdUnreadyDraws } from './effects/Precompiler'
import { ShadowPolicy } from './effects/ShadowPolicy'
import { FpsMeter } from '../ui/FpsMeter'

// Renderer creation is single-flight and falls back: webgpu first, webgl
// when init rejects (flaky mobile gpu, lost device, iOS under memory
// pressure), and the same cached promise handed back on every call so a
// rejecting init can never be re-invoked into a loop by provider
// re-renders. Both failure modes log with a [world] prefix — if a
// [crash] (world) ever appears WITHOUT one of these lines above it, the
// renderer is innocent and the hunt moves to fiber itself.
let rendererPromise = null

// The shadow pass draws every caster with one shared material, and three
// (r182, and still in the latest release) copies each caster's alphaTest
// onto it before the draw. Material's alphaTest setter bumps the version
// whenever the value crosses zero, so one alpha-clipped caster among the
// rest (the allotment's plants are 25 of them) moved that version every
// frame, and three answers a moved version by rebuilding the cache key of
// every caster drawn after it: 383 rebuilds a frame, 5.4 ms of an 11 ms
// frame on an M4 Pro, 14 ms at 4x CPU. Measured in the live page, 28 Oct
// (docs/world-performance.md).
//
// The fix is to let the assignment land without the bump. Each caster's
// render object was created with its own alpha test, so nothing it needs
// changes, and three's cache key reads the same value back. The accessor
// is non-enumerable because the cache key walks Object.keys plus the
// prototype's getters, and an own enumerable key would change it. The one
// case this misses: an object whose own alphaTest crosses zero at runtime
// keeps the shadow it had. Nothing in the town does that.
function steadyShadowMaterial(renderer) {
  const renderObject = renderer.renderObject
  const steadied = new WeakSet()
  renderer.renderObject = function (object, scene, ...rest) {
    const o = scene.overrideMaterial
    if (o !== null && o.isShadowPassMaterial === true && !steadied.has(o)) {
      steadied.add(o)
      Object.defineProperty(o, 'alphaTest', {
        configurable: true,
        get() { return this._alphaTest },
        set(v) { this._alphaTest = v },
      })
    }
    return renderObject.call(this, object, scene, ...rest)
  }
}

function createRenderer(props) {
  rendererPromise ||= (async () => {
    const setup = (renderer, backend) => {
      renderer.shadowMap.enabled = true
      renderer.shadowMap.type = THREE.BasicShadowMap
      steadyShadowMaterial(renderer)
      holdUnreadyDraws(renderer)
      // Which path actually ran, readable from any console. A phone report
      // ("everything is grey") gets diagnosed by asking for this one value.
      if (typeof window !== 'undefined') window.__renderer = backend
      return renderer
    }
    // ?gl=1 skips webgpu and runs the fallback path on purpose: it is how
    // a "world looks wrong on my phone" report gets diagnosed without the
    // phone, and how the fallback gets exercised on a machine whose webgpu
    // works fine.
    const forced = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('gl') === '1'
    try {
      if (forced) throw new Error('?gl=1, webgl fallback requested')
      const renderer = new THREE.WebGPURenderer(props)
      await renderer.init()
      return setup(renderer, 'webgpu')
    } catch (err) {
      console.error('[world] webgpu init failed, trying webgl:', err?.message || err)
      if (typeof window !== 'undefined') window.__renderer = 'webgl-initialising'
      const renderer = new THREE.WebGPURenderer({ ...props, forceWebGL: true })
      await renderer.init()
      return setup(renderer, 'webgl')
    }
  })()
  rendererPromise.catch((err) => console.error('[world] renderer init failed outright:', err?.message || err))
  return rendererPromise
}

// memo, and the whole diagnosis is in why: while the async webgpu init is
// pending, r3f's Canvas parks on an internal Block component that flips
// Canvas state from a LAYOUT EFFECT every time it mounts or unmounts. On a
// warm cache the world data starts flowing during that exact window, every
// store delta re-rendered Home, every re-render churned the suspended
// Canvas, and fifty synchronous Block flips in one cascade is the during-
// load "maximum update depth" crash that outlived the wall fix (throw
// stack: react-dom ← fiber events-*.esm.js:72, at 2s uptime). MainCanvas
// takes no props, so memo makes the world root render exactly once and
// none of Home's churn ever reaches the canvas again — which is also just
// free performance.
function MainCanvas() {
    // The world mounts only once the renderer promise settles. memo was
    // half the cure: it stopped Home churning the Canvas from outside,
    // but fiber's Block also flips Canvas state from its unmount CLEANUP,
    // so any deletion inside the canvas during the async-gl window (a
    // suspense fallback clearing as a warm-cache asset lands) re-enters
    // the same loop from inside. The dev overlay finally named it:
    // Block.useIsomorphicLayoutEffect → commitHookLayoutUnmountEffects →
    // a deletion cascade fifty deep. With no children until gl resolves,
    // nothing can suspend or be deleted in that window, so Block mounts
    // once and unmounts once. Boot cost is renderer init (~1s) serialised
    // ahead of asset loads; the veil covers it either way.
    const [glReady, setGlReady] = useState(false)
    // 0.8 everywhere webgpu runs; the webgl fallback drops to 0.6. The
    // devices that land on webgl are exactly the ones short on fill rate,
    // and 0.6 is ~44% fewer fragments. The world is nearest-filtered
    // pixel art anyway, so lower resolution reads as chunkier pixels, not
    // mush. Settled before children mount (the promise resolves first),
    // so the webgpu path never sees a dpr change.
    const [dpr, setDpr] = useState(0.8)
    useEffect(() => {
        let alive = true
        // Chain onto the promise fiber's own factory call created; never
        // seed it from here, this effect has no canvas props to give it.
        // Child effects run before parent effects so it exists by now;
        // the retry covers any exotic mount order.
        const chain = () => {
            if (!alive) return
            if (rendererPromise) {
                rendererPromise.then(() => {
                    if (!alive) return
                    if (window.__renderer === 'webgl') setDpr(0.6)
                    setGlReady(true)
                }, () => {})
                return
            }
            setTimeout(chain, 50)
        }
        chain()
        return () => { alive = false }
    }, [])

    // touch-action none: the browser must never interpret a walk or a
    // look drag as scroll/zoom; TouchControls owns world gestures.
    return (
        <div className="fixed top-0 left-0 w-screen h-screen" data-walk-surface style={{ touchAction: 'none' }}>

            <Canvas
                // "basic", not a bare `shadows`: fiber configures the
                // renderer after setup() and a bare prop sets PCFSoft over
                // our Basic, which the town ran on until 28 Oct without
                // anyone asking for it (about nine depth samples a lit
                // pixel instead of one, and soft edges on a pixel-art town).
                shadows="basic"
                dpr={dpr}
                camera={{ position: [0, 22, 48], fov: 55, near: 0.1, far: 600 }}
                // Default forward lighting. Night uses NO real per-lamp
                // point lights: three's TiledLighting mis-culled them
                // (view-dependent dropouts) and plain forward lighting for
                // ~20-30 points dropped to 30fps. Night is stylised instead
                // — blue moon directional (lights.jsx) + painted ground
                // pools (GridMaterial) + glowing bulbs (StreetGlow). Don't
                // re-add per-lamp point lights.
                gl={createRenderer}
            >
                {/* The Suspense is the other half of the fix, and it is not
                    decoration. Fiber's root wraps children in ITS OWN
                    Suspense whose fallback (Block) setStates the DOM-side
                    Canvas on every mount AND unmount. The world tree had no
                    boundary of its own, so every useGLTF/useTexture
                    suspension bubbled to fiber's root and cycled Block; a
                    warm cache resolves those in rapid synchronous
                    succession, fifty nested DOM updates, crash. With this
                    boundary the loaders suspend HERE, inside the r3f tree,
                    and Block only ever cycles once for the gl promise.
                    Never remove it, and never mount a sibling of this
                    Suspense that can suspend. */}
                {glReady && (
                    <Suspense fallback={null}>
                        <MainScene />
                        <Lights />
                        <RendererDither />
                        {/* The post chain: bloom on trial behind ?bloom=1, the trip
                            smear while he is up, judged with ?fps=1. Mounted always
                            and renders null until something asks for it, because the
                            trip arrives from the sim mid-session and a component that
                            is not mounted cannot notice that. */}
                        <PostFX />
                        {/* Compiles the pipelines of anything new (an
                            offering, a poster, a print) before it is looked
                            at, so it is never compiled in the frame you turn
                            to face it. See the file for the measurement. */}
                        <Precompiler />
                        {/* Small things stop casting: the shadow pass draws every
                            caster in town every frame. See the file. */}
                        <ShadowPolicy />
                    </Suspense>
                )}

                {/* <OrbitControls /> */}
            </Canvas>

            <FpsMeter />
        </div>
    )
}

export default memo(MainCanvas)
